import { get } from 'lodash'
import {
  ENTITY_TYPE_OPTIONS,
  STATE_OPTIONS,
  INTERESTED_PARTY_FORM_TYPE,
} from '../constants'
import { signerTitleOptions } from './forms'

const INTERESTED_PARTIES_LEVEL = 1

export const entityFormFields = pathPrefix => [
  {
    name: `${pathPrefix}.entityName`,
    label: 'Entity Name',
    elmId: `${pathPrefix}_entity_name`,
    type: 'text',
  },
  {
    name: `${pathPrefix}.entityType`,
    label: 'Type',
    elmId: `${pathPrefix}_entity_type`,
    type: 'select',
    options: ENTITY_TYPE_OPTIONS,
  },
  {
    name: `${pathPrefix}.state`,
    label: 'What state is your company registered in?',
    elmId: `${pathPrefix}_state`,
    type: 'select',
    options: STATE_OPTIONS,
  },
  {
    name: `${pathPrefix}.interestedPartyTitle`,
    label: 'Title',
    elmId: `${pathPrefix}_title`,
    type: 'select',
    options: ({ values }) => {
      const entityType = get(values, `${pathPrefix}.entityType`)
      return signerTitleOptions(entityType)
    },
  },
  {
    name: `${pathPrefix}.interestedPartyOtherTitle`,
    label: 'Other Title',
    elmId: `${pathPrefix}_other_title`,
    type: 'text',
    conditional: ({ values }) => {
      const interestedPartyTitle = get(
        values,
        `${pathPrefix}.interestedPartyTitle`
      )
      return interestedPartyTitle === 'OTHER'
    },
  },
  {
    name: `${pathPrefix}.interestedPartyOwnership`,
    label: 'Ownership',
    elmId: `${pathPrefix}_ownership`,
    type: 'percent',
  },
]

export const individualFormFields = (pathPrefix, multiEntityData) => [
  {
    name: `${pathPrefix}.name`,
    label: 'Name',
    elmId: `${pathPrefix}_name`,
    type: 'text',
  },
  {
    name: `${pathPrefix}.individualTitle`,
    label: 'Title',
    elmId: `${pathPrefix}_title`,
    type: 'select',
    options: ({ values }) => {
      // navigate tree using path prefix until closest entity to get entityType
      const ancestors = pathPrefix.split('.')
      let treePath = ancestors[0]

      // assume the root is an entity with entityType
      let entityType = get(multiEntityData, `${treePath}.entityType`)

      ancestors.slice(1).forEach(ancestor => {
        treePath = `${treePath}.${ancestor}`
        if (
          get(multiEntityData, `${treePath}.subFormType`) ===
          INTERESTED_PARTY_FORM_TYPE
        ) {
          entityType = get(multiEntityData, `${treePath}.entityType`)
        }
      })
      return signerTitleOptions(entityType)
    },
  },
  {
    name: `${pathPrefix}.individualOtherTitle`,
    label: 'Other Title',
    elmId: `${pathPrefix}_other_title`,
    type: 'text',
    conditional: ({ values }) => {
      const interestedPartyTitle = get(values, `${pathPrefix}.individualTitle`)
      return interestedPartyTitle === 'OTHER'
    },
  },
  {
    name: `${pathPrefix}.individualOwnership`,
    label: 'Ownership',
    elmId: `${pathPrefix}_ownership`,
    type: 'percent',
  },
  {
    name: `${pathPrefix}.includeSignature`,
    label: 'Include signature on contract',
    elmId: `${pathPrefix}_include_signature`,
    type: 'bool',
  },
  {
    name: `${pathPrefix}.email`,
    label: 'E-mail Address',
    elmId: `${pathPrefix}_email`,
    type: 'text',
  },
]

const getInterestedPartyFields = (
  interestedParty,
  interestedPartyPathPrefix,
  level,
  multiEntityData
) => {
  let result = {}

  if (interestedParty.subFormType === INTERESTED_PARTY_FORM_TYPE) {
    result = {
      label: 'Interested Party',
      type: 'group',
      fields: entityFormFields(interestedPartyPathPrefix),
      elmId: `${interestedPartyPathPrefix}_group`,
    }
  } else {
    result = {
      label: 'Individual',
      type: 'subGroup',
      child: true,
      level,
      fields: individualFormFields(interestedPartyPathPrefix, multiEntityData),
      elmId: `${interestedPartyPathPrefix}_subgroup`,
    }

    if (level === INTERESTED_PARTIES_LEVEL) {
      result.type = 'group'
      result.child = false
    }
  }
  if (interestedParty.interestedParties) {
    const nextInterestedParties = get(interestedParty, 'interestedParties', [])
    result.fields = result.fields.concat(
      nextInterestedParties.map((childInterestedParty, index) => {
        const childPathPrefix = `${interestedPartyPathPrefix}.interestedParties[${index}]`
        return getInterestedPartyFields(
          childInterestedParty,
          childPathPrefix,
          level + 1,
          multiEntityData
        )
      })
    )
  }
  return result
}

export const getMultiEntityFormFields = multiEntityData => {
  /**
   * In here we want to return a schema for summary modal based on multiEntityData
   * MultiEntityData is a datastructure shaped as a tree
   * { parent: {
   *  entityType,
   *  entityName,
   *  interestedParties: [{
   *    entityType,
   *    entityName,
   *    INTERESTED_PARTY,
   *    interestedParties: [{
   *      INDIVIDUAL
   *    }]}, {
   *    INDIVIDUAL
   *  }]
   * }}
   *
   * The idea is to navigate the structure and return the right piece of schema
   * depending on the node's type which could be INTERESTED_PARTY or INDIVIDUAL
   * for that you will need to pass the path to be able to pull the data and
   * in some cases pass the whole multientity data to do some calculations
   * on helper functions within the schema
   */

  let multiEntityFormFields = []
  const PARENT_PATH_PREFIX = 'parent'
  const LEVEL = 0
  const parentEntityFields = entityFormFields(PARENT_PATH_PREFIX)
  multiEntityFormFields.push({
    label: 'Parent Entity',
    type: 'group',
    fields: parentEntityFields.slice(0, parentEntityFields.length - 1),
  })

  const childInterestedParties = get(
    multiEntityData,
    'parent.interestedParties',
    []
  ).map((interestedParty, index) => {
    const interestedPartyPathPrefix = `${PARENT_PATH_PREFIX}.interestedParties[${index}]`
    return getInterestedPartyFields(
      interestedParty,
      interestedPartyPathPrefix,
      LEVEL + 1,
      multiEntityData
    )
  })

  multiEntityFormFields = multiEntityFormFields.concat(childInterestedParties)

  return multiEntityFormFields
}
